import React, { ReactNode } from "react"
import i18next from "i18next"
import { Updater } from "widgets-for-react"
import { FilterDate, FilterGroup, FilterSearch, FilterSelect, FiltersMap, FilterTab } from "./filters.state"
import { capitalize } from "@shared/Frontend/utils/capitalize"
import { pushDatalayer } from "@shared/Frontend/seo/datalayer"
import { FormControls } from "@shared/Frontend/forms"
import { updateFilterDate, updateFilterSingleValue, updateFilterSearch } from "./filters.utils"
import dayjs from "dayjs"
import { TeaserKind } from "../teasers/teasers.state"
import { translate } from "@shared/Frontend/utils/translate"

const allLabel = translate('overviews:filter:all', {}, 'All')

export type FiltersProps = {
  kind: TeaserKind
  filters: FiltersMap
  setFilter: (key: string, updater: Updater<FilterGroup>) => void
  clearFilters: () => void
}
export const Filters = (props: FiltersProps): ReactNode => {
  const { kind, filters, setFilter, clearFilters } = props

  if (!filters) return <></>

  return <div className='overview__filter'>
    <div className="filter">
      <div className='filter__inner'>
        <div className={`filter__form ${kind == 'search' ? 'searchform' : ''}`}>
          {filters
            .entrySeq()
            .map(([key, group]) => (
              <FilterItemLayout
                group={group}
                key={key}
                groupKey={key}
                setFilter={setFilter}
              />
            ))}
        </div>
      </div>
    </div>
  </div>
}

type SetFilter<TGroup> = (key: string, updater: Updater<TGroup>) => void
type FilterItemProps<TGroup extends FilterGroup> = {
  group: TGroup
  groupKey: string
  setFilter: SetFilter<TGroup>
}
export const FilterItemLayout = (props: FilterItemProps<FilterGroup>) => {
  const { group, groupKey, setFilter } = props

  switch (group.kind) {
    case "select": return <FilterItemSelect
      setFilter={setFilter as SetFilter<typeof group>}
      groupKey={groupKey}
      group={group}
    />

    case "tab": 
      return <FilterItemTab
      setFilter={setFilter as SetFilter<typeof group>}
      groupKey={groupKey}
      group={group}
    />

    case "search": return <FilterItemSearch
      setFilter={setFilter as SetFilter<typeof group>}
      groupKey={groupKey}
      group={group}
    />

    case "date": return <FilterItemDate
      setFilter={setFilter as SetFilter<typeof group>}
      groupKey={groupKey}
      group={group}
    />

    default: return <></>
  }
}

export const FilterItemTab = (props: FilterItemProps<FilterTab>) => {
  const { group, groupKey, setFilter } = props
  const filterItems = group.filterItems

  const categoryLabel = translate(`overviews:filter:groups:${groupKey}`, {}, '')

  const items = filterItems.map((c, index) => {
    const item = c.value

    return {
      controlId: `sgCheck${groupKey}${capitalize(item)}`,
      label: item,
      checked: c.isSelected,
      onChange: () => {
        setFilter(groupKey, f0 => updateFilterSingleValue(f0, index))

        pushDatalayer({
          category: 'select_filter',
          action: 'change_filter',
          label: item
        })
      },
    }
  })

  return <FormControls.FormCheckInGroup
    key={groupKey}
    controlId={`sgCheck${groupKey}`}
    label={typeof categoryLabel == 'string' ? categoryLabel : ''}
    disabled={false}
    required={true}
    invalid={false}
    fieldMessage={""}
    items={[
      {
        controlId: `sgCheck${groupKey}all`,
        label: allLabel,
        checked: !filterItems.some(i => i.isSelected),
        onChange: () => {
          setFilter(groupKey, f0 => updateFilterSingleValue(f0, -1))

          pushDatalayer({
            category: 'select_filter',
            action: 'change_filter',
            label: `${categoryLabel} filters cleared`
          })
        }
      },
      ...items
    ]}
  />
}

export const FilterItemSelect = (props: FilterItemProps<FilterSelect>) => {
  const { group, groupKey, setFilter } = props
  const filterItems = group.filterItems

  const categoryLabel = translate(`overviews:filter:groups:${groupKey}`, {}, '')
  
  return <FormControls.FormSelectInGroup
    key={groupKey}
    controlId={`sgChecks${groupKey}`}
    label={typeof categoryLabel == 'string' ? categoryLabel : ''}
    disabled={false}
    required={true}
    invalid={false}
    value={filterItems.find(item => item.isSelected)?.value ?? ''}
    onChange={(newItem) => { 
      const [index, filterItem] = filterItems.findEntry(item => item.value === newItem) || []

      setFilter(groupKey, f0 => updateFilterSingleValue(f0, index ?? -1))

      pushDatalayer({
        category: 'select_filter',
        action: 'use_filter',
        label: filterItem ? filterItem.value : allLabel
      })      
    }}
    options={
      [
        { value: '', label: allLabel, disabled: false},
        ...filterItems.map(item => ({
          value: item.value, label: item.value
        })).toArray()
      ]
    }
  />
}

export const FilterItemSearch = (props: FilterItemProps<FilterSearch>) => {
  const { group: f, groupKey, setFilter } = props

  const categoryLabel = translate(`overviews:filter:groups:${groupKey}`, {}, '')

  return <FormControls.FormItemInGroup
    key='sg-pf-1'
    type="text"
    controlId='sgInput-sg-pf-1'
    inputMode="search"
    label={categoryLabel}
    disabled={false}
    required={false}
    placeholder={translate(`overviews:search:placeholder`, {}, '')}
    value={f.searchQuery}
    onChange={e => {
      setFilter(groupKey, f0 => updateFilterSearch(f0, e))
      pushDatalayer({
        category: 'search_filter',
        action: 'use_filter',
        label: "search_input"
      })
    }}
    invalid={false}
    labelIsSrOnly={true}
    extraGroupClasses='fs__group--search'
  />
}

export const FilterItemDate = (props: FilterItemProps<FilterDate>) => {
  const { group, groupKey, setFilter } = props
  const { startDate, endDate } = group

  const categoryLabel = translate(`overviews:filter:groups:${groupKey}`, {}, '')

  const currentDate = new Date();
  const minDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)}-${currentDate.getDate()}`;
  return <>
    <div className='fs__group fs__group--input fs__group--date'>
      <label className="fs__label text--md  form-label" htmlFor="sgDate--CFOB-1s">{i18next.t<string>("finder:overview.filter:startDateFilter")}</label>
      <input
        ref={ref => {
          if (!ref) return
          if (ref.valueAsDate) return
          if (!startDate) return
          ref.defaultValue = dayjs(startDate).format("YYYY-MM-DD")
        }}
        type="date"
        id="sgDate--CFOB-1s"
        name="sgDate--CFOB-1s"
        className="fs__input form-control"
        min={minDate}
        onChange={e => {
          if (e.target.valueAsDate) {
            setFilter(groupKey, f0 => updateFilterDate(f0, e.target.valueAsDate, "start"))
          }
          pushDatalayer({
            category: 'date_filter',
            action: 'use_filter',
            label: "search_input"
          })
        }}
      />
    </div>

    <div className='fs__group fs__group--input fs__group--date'>
      <label className="fs__label text--md  form-label" htmlFor="sgDate--CFOB-1s">{i18next.t<string>("finder:overview.filter:endDateFilter")}</label>
      <input
        ref={ref => {
          if (!ref) return
          if (ref.valueAsDate) return
          if (!endDate) return
          ref.defaultValue = dayjs(endDate).format("YYYY-MM-DD")
        }}
        type="date"
        id="sgDate--CFOB-1e"
        name="sgDate--CFOB-1e"
        className="fs__input form-control"
        min={minDate}
        onChange={e => {
          if (e.target.valueAsDate) {
            setFilter(groupKey, f0 => updateFilterDate(f0, e.target.valueAsDate, "end"))
          }
          pushDatalayer({
            category: 'date_filter',
            action: 'use_filter',
            label: "search_input"
          })
        }}
      />
    </div>
  </>
}
